'use client'

import React from 'react'
import classNames from 'classnames'
import { Title, type HeadingTag } from '@/ui/components/Title'
import RichText from '@/ui/components/RichText'
import { Button, ButtonContent } from '@/ui/atoms/Button'
import Link from 'next/link'
import { SerializedEditorState } from '@payloadcms/richtext-lexical/lexical'
import { Icon } from '@/ui/atoms/Icon'

interface CTABlockProps {
  variant?: 'default' | 'split'
  alignment?: 'left' | 'center'
  title: string
  titleHierarchy?: HeadingTag
  description?: string | SerializedEditorState
  features?: string[]
  showNewsletter?: boolean
  customButtons?: React.ReactNode
  className?: string
}

export const CTABlock: React.FC<CTABlockProps> = ({
  variant = 'default',
  alignment = 'center',
  title,
  titleHierarchy = 'h2',
  description,
  features = [],
  showNewsletter = false,
  customButtons,
  className,
}) => {
  const isSplit = variant === 'split'
  const isCenter = alignment === 'center'
  const hasFeatures = features.length > 0
  const hasTwoColumns = isSplit && hasFeatures && (description || title)

  const handleSmoothScroll = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    const href = e.currentTarget.getAttribute('href')
    if (href?.startsWith('#')) {
      const targetId = href.substring(1)
      const targetElement = document.getElementById(targetId)
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }
  }

  return (
    <div className={classNames('w-full', className)}>
      <div
        className={classNames(
          'relative overflow-hidden bg-bg-subtle border border-border-subtle rounded-lg',
        )}
      >
        {/* Decorative gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-97/30 to-transparent pointer-events-none" />

        {/* Main Content */}
        <div
          className={classNames(
            'relative z-10 p-8 lg:p-12',
            isCenter && !isSplit ? 'flex flex-col items-center text-center' : '',
            !isSplit ? 'flex flex-col justify-center' : '',
            hasTwoColumns ? 'lg:grid lg:grid-cols-2 lg:gap-8' : 'flex flex-col',
          )}
        >
          {/* Left Column: Title and Description */}
          <div>
            {/* Title */}
            <div
              className={classNames(
                'mb-6',
                isCenter && !isSplit ? 'text-center' : '',
              )}
            >
              <Title
                className="!text-headline-M-semibold text-text-base"
                hierarchy={titleHierarchy}
              >
                {title}
              </Title>
            </div>

            {/* Description */}
            {description && (
              <div
                className={classNames(
                  hasTwoColumns ? 'mb-6 lg:mb-0' : 'mb-8',
                  !hasTwoColumns && isCenter && !isSplit ? 'max-w-2xl' : '',
                  !hasTwoColumns && !isCenter ? 'max-w-prose' : '',
                )}
              >
                {typeof description === 'string' ? (
                  <p className="text-body-L-regular text-text-subtle leading-relaxed">
                    {description}
                  </p>
                ) : (
                  <RichText
                    data={description}
                    className="text-body-L-regular text-text-subtle prose-p:!text-body-L-regular prose-p:!text-text-subtle prose-p:!mb-0 prose-p:!leading-relaxed"
                    enableGutter={false}
                    enableProse={false}
                  />
                )}
              </div>
            )}
          </div>

          {/* Right Column: Features List */}
          {hasFeatures && (
            <ul
              className={classNames(
                'space-y-3.5',
                !hasTwoColumns && 'mb-8',
                isCenter && !isSplit ? 'text-left inline-block' : '',
              )}
            >
              {features.map((feature, index) => (
                <li key={index} className="flex items-start gap-3">
                  <span className="flex-shrink-0 rounded-sm bg-soft-gradient-blue size-5 flex items-center justify-center mt-1">
                    <Icon name="ri-check-line" className="!size-3 text-white" />
                  </span>
                  <span className="text-body-M-regular text-text-base flex-1">
                    {feature}
                  </span>
                </li>
              ))}
            </ul>
          )}

          {/* Buttons */}
          {customButtons ? (
            customButtons
          ) : showNewsletter ? (
            <div
              className={classNames(
                'flex flex-col sm:flex-row gap-3 mt-8',
                isCenter ? 'justify-center' : '',
                hasTwoColumns ? 'lg:col-span-2' : '',
              )}
            >
              <Button
                asChild
                variant="primary"
                size="md"
                className="w-full sm:w-auto shadow-sm hover:shadow-md transition-shadow"
              >
                <Link href="/login">
                  <ButtonContent label="Access Platform" />
                </Link>
              </Button>
              <Button
                asChild
                variant="secondary"
                size="md"
                className="w-full sm:w-auto shadow-sm hover:shadow-md transition-shadow"
              >
                <a href="#newsletter" onClick={handleSmoothScroll}>
                  <ButtonContent label="Subscribe" />
                </a>
              </Button>
            </div>
          ) : null}

        </div>
      </div>
    </div>
  )
}
